import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

export interface DropdownOption {
  value: string;
  label: React.ReactNode | string;
  color?: string;
}

export interface DropdownProps {
  value: string;
  onChange: (value: string) => void;
  options: DropdownOption[];
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
  searchable?: boolean;
  onSearchChange?: (term: string) => void;
  onScrollEnd?: () => void;
  isLoadingMore?: boolean;
  hideIcon?: boolean;
  trigger?: React.ReactNode;
  align?: 'left' | 'right';
}

export function Dropdown({
  value,
  onChange,
  options,
  placeholder = 'Select an option',
  className,
  style,
  searchable = false,
  onSearchChange,
  onScrollEnd,
  isLoadingMore,
  hideIcon = false,
  trigger,
  align = 'left'
}: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [direction, setDirection] = useState<'down' | 'up'>('down');
  const [localSearch, setLocalSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const toggleDropdown = () => {
    if (!isOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      if (spaceBelow < 250) {
        setDirection('up');
      } else {
        setDirection('down');
      }
    }
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);
    if (!newIsOpen) {
      setLocalSearch('');
      if (onSearchChange) onSearchChange('');
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setLocalSearch('');
        if (onSearchChange) onSearchChange('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find(opt => opt.value === value);

  const displayedOptions = searchable && !onSearchChange 
    ? options.filter(opt => typeof opt.label === 'string' && opt.label.toLowerCase().includes(localSearch.toLowerCase()))
    : options;

  return (
    <div style={{ position: 'relative', ...style }} className={className} ref={dropdownRef}>
      {trigger ? (
        <div onClick={toggleDropdown} style={{ cursor: 'pointer', display: 'inline-flex' }}>
          {trigger}
        </div>
      ) : (
        <div 
          onClick={toggleDropdown}
          style={{
            padding: '6px 12px',
            borderRadius: '0',
            fontSize: '12px',
            fontWeight: 600,
            backgroundColor: 'transparent',
            border: '1px solid var(--admin-card-border)',
            transition: 'all 0.2s',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            color: value ? 'var(--admin-card-text)' : 'var(--admin-card-subtext)'
          }}
        >
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, textAlign: 'left' }}>
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          {!hideIcon && <ChevronDown size={14} style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', flexShrink: 0 }} />}
        </div>
      )}
      
      {isOpen && (
        <div style={{ 
          position: 'absolute', 
          top: direction === 'down' ? 'calc(100% + 6px)' : 'auto', 
          bottom: direction === 'up' ? 'calc(100% + 6px)' : 'auto', 
          left: align === 'right' ? 'auto' : 0, 
          right: align === 'right' ? 0 : 0, 
          minWidth: align === 'right' ? '190px' : 'auto',
          background: 'var(--color-dropdown-glass, rgba(10, 15, 20, 0.7))',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          border: '1px solid var(--color-border-subtle, rgba(255, 255, 255, 0.1))', 
          borderRadius: '0', 
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)', 
          zIndex: 50, 
          display: 'flex', 
          flexDirection: 'column', 
          maxHeight: '250px', 
          overflow: 'hidden' 
        }}>
          {searchable && (
            <div style={{ padding: '8px', borderBottom: '1px solid var(--admin-card-border)' }}>
              <input 
                type="text" 
                placeholder="Search..." 
                value={onSearchChange ? undefined : localSearch}
                onChange={(e) => {
                  if (onSearchChange) onSearchChange(e.target.value);
                  else setLocalSearch(e.target.value);
                }}
                onClick={(e) => e.stopPropagation()}
                style={{ width: '100%', padding: '8px 12px', borderRadius: '0', border: '1px solid var(--admin-card-border)', background: 'var(--admin-input-bg)', color: 'var(--admin-card-text)', fontSize: '13px', outline: 'none' }}
              />
            </div>
          )}
          <div 
            style={{ padding: '4px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '2px' }}
            onScroll={(e) => {
              if (!onScrollEnd || isLoadingMore) return;
              const target = e.currentTarget;
              if (target.scrollHeight - target.scrollTop <= target.clientHeight + 20) {
                onScrollEnd();
              }
            }}
          >
            {displayedOptions.length > 0 ? displayedOptions.map(opt => (
              <div 
                key={opt.value}
                onClick={() => { 
                  onChange(opt.value); 
                  setIsOpen(false); 
                  setLocalSearch('');
                  if (onSearchChange) onSearchChange('');
                }}
                style={{ 
                  padding: '8px 12px', 
                  fontSize: '12px', 
                  fontWeight: 500, 
                  cursor: 'pointer', 
                  borderRadius: '0', 
                  color: opt.color ? opt.color : (value === opt.value ? 'var(--admin-accent-green)' : 'var(--admin-card-text)'), 
                  backgroundColor: value === opt.value ? 'var(--color-glass-surface, rgba(255,255,255,0.05))' : 'transparent', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'flex-start',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => { if (value !== opt.value) e.currentTarget.style.backgroundColor = 'var(--color-glass-subtle, rgba(255,255,255,0.02))' }}
                onMouseLeave={(e) => { if (value !== opt.value) e.currentTarget.style.backgroundColor = 'transparent' }}
              >
                {opt.label}
              </div>
            )) : (
              <div style={{ padding: '12px', textAlign: 'center', fontSize: '13px', color: 'var(--admin-card-subtext)' }}>
                No options found
              </div>
            )}
            {isLoadingMore && (
              <div style={{ padding: '12px', textAlign: 'center', fontSize: '13px', color: 'var(--admin-card-subtext)' }}>
                Loading more...
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
